import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Settings,
  User,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CLERK_ENABLED } from "@/lib/clerk";
import { LanguageSubmenu } from "@/components/LanguageSubmenu";
import { useTranslation } from "../hooks/useTranslation";

export function NavUser({ user }) {
  const { isMobile } = useSidebar();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Generate user initials for avatar fallback
  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2) || "U"
    );
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="hover:bg-[#F1F5F9] data-[state=open]:bg-white data-[state=open]:shadow-[0_1px_2px_rgba(15,23,42,0.08),0_0_0_1px_#E2E8F0] [&>svg]:text-[#94A3B8]"
            >
              <Avatar className="h-8 w-8 rounded-full">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-full bg-[#E2E8F0] text-xs font-semibold text-[#334155]">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold text-[#020617]">{user.name}</span>
                <span className="truncate text-xs text-[#64748B]">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-full">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-full bg-[#E2E8F0] text-xs font-semibold text-[#334155]">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                  {/*user.currentTenant && (
                    <span className="truncate text-xs text-muted-foreground">
                      {user.currentTenant}
                    </span>
                  )*/}
                  {/*user.origin && (
                    <span className="truncate text-xs text-muted-foreground">
                      {user.origin === "sp"
                        ? "SSO Account"
                        : user.origin === "rag"
                        ? "RAG Account"
                        : "Mixed Account"}
                    </span>
                  )*/}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {/* Il profilo (nome, email, password) lo gestisce Clerk: senza
                  Clerk non c'e' una pagina profilo, e la voce non compare. */}
              {CLERK_ENABLED ? (
                <DropdownMenuItem onClick={() => window.Clerk?.openUserProfile?.()}>
                  <User />
                  {t("nav.profile", "Profile")}
                </DropdownMenuItem>
              ) : null}
              <LanguageSubmenu />
              <DropdownMenuItem onClick={() => navigate("/organization")}>
                <Settings />
                {t("common.settings", "Settings")}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {t("common.logout", "Logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
